import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAckQualificationCelebration,
  useQualificationCelebration,
  useRegister,
} from '../../api/hooks';
import { useCelebrationsIdle } from '../../celebrationGate';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import { shortDate, timeLabel } from '../../format';
import type { QualCelebration } from '../../api/types';

/**
 * Пост-гейм рекап отбора (Q3): полноэкранный, 5 кадров, показывается один раз
 * после сыгранного турнира — СТРОГО после листов достижений/уровня
 * (celebrationGate). Тап — сразу финальный кадр; prefers-reduced-motion — без
 * анимаций; haptics только на вход в зону и завершение калибровки.
 */
export function QualificationRecapSheet({ enabled }: { enabled: boolean }) {
  const celebrationsIdle = useCelebrationsIdle();
  const ready = enabled && celebrationsIdle;
  const celebration = useQualificationCelebration(enabled);
  const ack = useAckQualificationCelebration();
  const [dismissed, setDismissed] = useState(false);
  const [frame, setFrame] = useState(0);
  const reduced = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );
  const hapticsFired = useRef(false);

  const c = celebration.data ?? null;
  const visible = ready && !dismissed && c !== null;
  const FRAMES = 5;
  const last = frame >= FRAMES - 1;

  // Автопродвижение кадров по таймингам хэндоффа.
  useEffect(() => {
    if (!visible) return;
    if (reduced) {
      setFrame(FRAMES - 1);
      return;
    }
    if (last) return;
    const durations = [1600, 2900, 1400, 1400];
    const t = setTimeout(() => setFrame((f) => Math.min(f + 1, FRAMES - 1)), durations[frame] ?? 1500);
    return () => clearTimeout(t);
  }, [visible, frame, last, reduced]);

  useEffect(() => {
    if (!visible || !c) return;
    if (frame === 0) track('qualification_recap_shown', { gameId: c.gameId });
    if (!hapticsFired.current && (c.flags.enteredZone || c.flags.calibrationDone)) {
      hapticsFired.current = true;
      haptics.success();
    }
  }, [visible, frame, c]);

  if (!visible || !c) return null;

  const skip = () => {
    if (!last) {
      track('qualification_recap_skip', { fromFrame: frame });
      setFrame(FRAMES - 1);
    }
  };
  const done = () => {
    void ack.mutateAsync().catch(() => undefined);
    setDismissed(true);
  };

  return (
    <div className="q-recap" onClick={skip}>
      <div className="q-recap-top">
        <span className="q-recap-head mono">
          ОТБОР · ТУРНИР {shortDate(c.gameDate).toUpperCase()}
        </span>
        <span className="q-recap-dots">
          {Array.from({ length: FRAMES }, (_, i) => (
            <i key={i} className={i === frame ? 'on' : ''} />
          ))}
        </span>
      </div>

      <div className="q-recap-body">
        {frame === 0 && <Frame1 c={c} />}
        {frame === 1 && <Frame2 c={c} reduced={reduced} />}
        {frame === 2 && <Frame3 c={c} />}
        {frame === 3 && <Frame4 c={c} />}
        {frame === 4 && <Frame5 c={c} onDone={done} />}
      </div>

      {!last && <div className="q-recap-skip">тап — пропустить</div>}
    </div>
  );
}

function Frame1({ c }: { c: NonNullable<QualCelebration> }) {
  return (
    <div className="q-frame q-frame--in">
      {c.place !== null ? (
        <>
          <div className="q-recap-place mono">{c.place}</div>
          <div className="q-recap-of">место{c.fieldSize !== null && ` из ${c.fieldSize} игроков`}</div>
        </>
      ) : (
        <div className="q-recap-of">Турнир сыгран</div>
      )}
      {c.playersBeaten > 0 && <div className="q-recap-pill">обошёл {c.playersBeaten} игроков</div>}
    </div>
  );
}

function Frame2({ c, reduced }: { c: NonNullable<QualCelebration>; reduced: boolean }) {
  const b = c.breakdown;
  return (
    <div className="q-frame q-frame--in">
      <div className="q-recap-sub">Откуда очки</div>
      <div className="q-recap-rows">
        <div className={`row ${reduced ? '' : 'q-anim-row'}`} style={{ animationDelay: '0.1s' }}>
          <span>За результат</span>
          <span className={`mono ${b.resultShown < 0 ? 'muted' : 'up'}`}>
            {b.resultShown >= 0 ? `+${b.resultShown}` : b.resultShown}
          </span>
        </div>
        <div className={`row ${reduced ? '' : 'q-anim-row'}`} style={{ animationDelay: '0.55s' }}>
          <span>За участие</span>
          <span className="mono up">+{b.bonus}</span>
        </div>
        {b.floorShown > 0 && (
          <div className={`row ${reduced ? '' : 'q-anim-row'}`} style={{ animationDelay: '0.9s' }}>
            <span>Защита старта</span>
            <span className="mono">+{b.floorShown}</span>
          </div>
        )}
        <div className={`row total ${reduced ? '' : 'q-anim-row'}`} style={{ animationDelay: '1.25s' }}>
          <span>Итого</span>
          <span className={`mono ${b.total < 0 ? 'muted' : ''}`}>
            {b.total >= 0 ? `+${b.total}` : b.total}
          </span>
        </div>
      </div>
      <div className="q-recap-transition mono">
        {b.shownBefore} <span className="arr">→</span> {b.shownAfter}
      </div>
      <div className="q-recap-note">рейтинг отбора</div>
      {c.flags.atFloor && <div className="q-recap-note">Рейтинг {b.shownAfter} — ниже не опускается</div>}
      {c.flags.calibrating && (
        <div className="q-recap-note">
          {c.flags.calibrationProgress}/{c.flags.calGames} · в калибровке рейтинг меняется сильнее
        </div>
      )}
      {c.flags.calibrationDone && c.rankAfter !== null && (
        <div className="q-recap-note q-recap-note--green">Ты в зачёте · #{c.rankAfter}</div>
      )}
      {c.flags.beatRaceLeader && <div className="q-recap-note q-recap-note--brass">Обошёл лидера гонки</div>}
    </div>
  );
}

function Frame3({ c }: { c: NonNullable<QualCelebration> }) {
  if (c.rankBefore === null || c.rankAfter === null) {
    return (
      <div className="q-frame q-frame--in">
        <div className="q-recap-sub">Место в таблице</div>
        <div className="q-recap-move mono">{c.rankAfter !== null ? `#${c.rankAfter}` : 'после калибровки'}</div>
      </div>
    );
  }
  const up = c.rankBefore - c.rankAfter;
  return (
    <div className="q-frame q-frame--in">
      <div className="q-recap-move mono">
        #{c.rankBefore} <span className="arr">→</span> #{c.rankAfter}
        {up > 0 && <span className="q-recap-updelta"> · +{up} мест{up === 1 ? 'о' : up < 5 ? 'а' : ''}</span>}
      </div>
      <div className="q-recap-lb">
        {c.neighbors.map((n) => (
          <div key={n.rank} className={`row ${n.isMe ? 'me q-anim-rise' : ''}`}>
            <span className="rk mono">{n.rank}</span>
            <span className="nm">{n.name}</span>
            <span className="rt mono">{n.rating}</span>
          </div>
        ))}
      </div>
      {c.flags.enteredZone && <div className="q-recap-note q-recap-note--brass">Зона отбора</div>}
    </div>
  );
}

function Frame4({ c }: { c: NonNullable<QualCelebration> }) {
  const inZone = c.rankAfter !== null && c.rankAfter <= c.tickets;
  return (
    <div className="q-frame q-frame--in">
      <div className="q-recap-sub">{inZone ? 'Твоя позиция' : 'Новая дистанция'}</div>
      {inZone ? (
        <div className="q-recap-dist mono">Ты в зоне отбора</div>
      ) : (
        <>
          <div className="q-recap-dist">
            До #{c.tickets} — <span className="mono num">{c.gapAfter ?? '—'}</span>
          </div>
          {c.gapAfter !== null && c.gapAfter <= 25 && (
            <div className="q-recap-note">Хватит одного хорошего вечера</div>
          )}
          {c.gapBefore !== null && c.gapAfter !== null && c.gapBefore !== c.gapAfter && (
            <div className="q-recap-note muted">Было: до #{c.tickets} — {c.gapBefore} очков</div>
          )}
        </>
      )}
    </div>
  );
}

function Frame5({ c, onDone }: { c: NonNullable<QualCelebration>; onDone: () => void }) {
  const navigate = useNavigate();
  const t = c.nextTournament;
  const register = useRegister(t?.id ?? 0);
  return (
    <div className="q-frame q-frame--in" onClick={(e) => e.stopPropagation()}>
      {c.rankAfter !== null && (
        <div className="q-recap-summary mono">
          #{c.rankAfter}
          {c.rankAfter > c.tickets && c.gapAfter !== null && ` · до #${c.tickets} — ${c.gapAfter}`}
        </div>
      )}
      {t && (
        <div className="q-recap-next">
          <div className="q-recap-sub">Ближайший турнир отбора</div>
          <div className="when">
            {shortDate(t.startTime)} · {timeLabel(t.startTime)}
          </div>
          <div className="meta">
            <span className="up">+{c.breakdown.bonus} за участие</span> · {t.registeredCount} записались
          </div>
          {t.registeredByMe ? (
            <div className="q-next-btn q-next-btn--in q-recap-reg">✓ Ты записан</div>
          ) : (
            <button
              className="btn q-recap-reg"
              disabled={register.isPending}
              onClick={() => {
                track('qualification_register', { tournamentId: t.id, from: 'recap' });
                void register
                  .mutateAsync()
                  .then(() => haptics.success())
                  .catch(() => haptics.error());
              }}
            >
              {register.isPending ? 'Запись…' : 'Записаться'}
            </button>
          )}
        </div>
      )}
      <button
        className="q-recap-done"
        onClick={() => {
          navigate('/qualification/me');
          onDone();
        }}
      >
        Моя позиция
      </button>
      <button className="q-recap-done q-recap-done--primary" onClick={onDone}>
        Готово
      </button>
    </div>
  );
}
